import React from 'react';
import { NavLink } from 'react-router-dom';
import { useEntitlement } from '../../context/EntitlementContext';
import { PRO_FEATURES } from '../../config/entitlements';

// `proFeature` names the entitlement (config/entitlements.js) that gates the whole section.
const SECTIONS = [
  { path: 'overview', label: 'Overview', short: 'Overview', icon: '01' },
  { path: 'profile', label: 'Financial Profile', short: 'Profile', icon: '02' },
  { path: 'accumulate', label: 'Accumulate', short: 'Accumulate', icon: '03' },
  { path: 'spend-down', label: 'Spend Down', short: 'Spend Down', icon: '04' },
  { path: 'advanced', label: 'Age Explorer', short: 'Retire When', icon: '05', proFeature: 'advancedRetirement' },
  { path: 'compare', label: 'Compare Plans', short: 'Compare', icon: '06' },
  { path: 'reports', label: 'Reports', short: 'Reports', icon: '07' },
];

// True only where store billing is active and the user has not unlocked Pro,
// so nothing is tagged on the web build.
export function useSectionLocked() {
  const { isPro, billingEnabled } = useEntitlement();
  const locked = billingEnabled && !isPro;
  return (section) => locked && !!section?.proFeature && !!PRO_FEATURES[section.proFeature];
}

export function ProTag({ className = '' }) {
  return (
    <span
      // `relative` keeps the screen-reader text inside the tag. Without it the text is
      // placed against the page and widens it past a phone's screen.
      className={`relative flex-shrink-0 px-1.5 py-0.5 rounded border border-terminal-amber-dim text-terminal-amber text-[10px] leading-none font-mono uppercase tracking-wider ${className}`}
    >
      Pro<span className="sr-only"> subscription needed</span>
    </span>
  );
}

export default function SectionNav({ completionStatus = {} }) {
  const isLocked = useSectionLocked();
  return (
    <nav className="space-y-1">
      {SECTIONS.map((section) => {
        const isComplete = completionStatus[section.path];
        return (
          <NavLink
            key={section.path}
            to={section.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-mono uppercase tracking-wider transition-colors ${
                isActive
                  ? 'bg-terminal-dark-green/20 text-terminal-green border border-terminal-dark-green'
                  : 'text-txt-secondary hover:text-terminal-dim-green hover:bg-surface-elevated border border-transparent'
              }`
            }
          >
            <span className="flex-shrink-0 w-6 h-6 rounded-full border border-current flex items-center justify-center text-xs">
              {isComplete ? '✓' : section.icon}
            </span>
            <span className="truncate">{section.label}</span>
            {isLocked(section) && <ProTag className="ml-auto" />}
          </NavLink>
        );
      })}
    </nav>
  );
}

export { SECTIONS };
